// PdfProcessor.js - Handles PDF processing functionality

/**
 * Converts a PDF file to an array of image data
 * @param {File} file - The PDF file
 * @param {Function} setProcessingProgress - State updater for progress tracking
 * @returns {Promise<Array>} Array of page images with their metadata
 */
export const convertPDFToImages = async (file, setProcessingProgress) => {
  try {
    // Set worker source. Self-hosted (public/pdf.worker.min.js, matching the pdf.js
    // version pinned in public/index.html) rather than loaded from cdnjs: the worker is
    // handed the raw PDF bytes to parse, and a Worker constructed from a URL doesn't
    // support the `integrity` attribute the way a <script> tag does, so a compromised CDN
    // response here couldn't be caught the way the bootstrap script's SRI check would.
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
    
    // Convert file to array buffer
    const arrayBuffer = await file.arrayBuffer();
    
    // Load the PDF
    const pdf = await window.pdfjsLib.getDocument(arrayBuffer).promise;
    console.log(`PDF loaded with ${pdf.numPages} pages`);
    
    setProcessingProgress({
      currentPage: 0,
      totalPages: pdf.numPages,
      isProcessingPDF: true
    });
    
    // Array to store all page images
    const pageImages = [];
    
    // Process each page
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      setProcessingProgress(prev => ({
        ...prev,
        currentPage: pageNum
      }));
      
      const page = await pdf.getPage(pageNum);
      
      // Set up canvas for rendering
      const scale = 2.0; // Higher scale for better quality
      const viewport = page.getViewport({ scale });
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      // Render page to canvas
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      
      await page.render(renderContext).promise;
      
      // Convert canvas to data URL
      const imageData = canvas.toDataURL('image/png');
      pageImages.push({
        pageNum,
        imageData,
        width: viewport.width,
        height: viewport.height
      });
      
      console.log(`Processed page ${pageNum}/${pdf.numPages}`);
    }
    
    return pageImages;
    
  } catch (error) {
    console.error('Error processing PDF:', error);
    throw new Error('Failed to process PDF. Please try again with a different file.');
  } finally {
    setProcessingProgress({
      currentPage: 0,
      totalPages: 0,
      isProcessingPDF: false
    });
  }
};

/**
 * Process an image file and set it to state
 * @param {File} file - The image file
 * @returns {Promise<string>} The image data URL
 */
export const processImageFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target.result);
    };
    reader.onerror = (e) => {
      console.error('Error reading file:', e);
      reject(new Error('Failed to read the file. Please try again with a different file.'));
    };
    reader.readAsDataURL(file);
  });
};